import { Box, Dialog, DialogContent, IconButton, useMediaQuery, useTheme } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from "@mui/icons-material/Close";
import { FC, useState } from "react";
import { VideoCard, VideoCardProps } from "./video-card";

interface VideoCarouselProps {
  videos: VideoCardProps["video"][];
}

export const VideoCarousel: FC<VideoCarouselProps> = ({ videos }) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const isSm = useMediaQuery(theme.breakpoints.up("sm"));
  const cardsToShow = isMd ? 3 : isSm ? 2 : 1;
  const [start, setStart] = useState(0);
  const [activeVideo, setActiveVideo] = useState<{
    url: string;
    title: string;
  } | null>(null);

  const cardWidth = isMd ? 360 : 320; // px, should match VideoCard
  const gap = isMd ? 28 : 24; // px, spacing between cards
  const totalWidth = cardsToShow * cardWidth + (cardsToShow - 1) * gap;

  const maxStart = Math.max(0, videos.length - cardsToShow);

  const handlePrev = () => {
    setStart((prev) => Math.max(prev - cardsToShow, 0));
  };
  const handleNext = () => {
    setStart((prev) => Math.min(prev + cardsToShow, maxStart));
  };

  const canPrev = start > 0;
  const canNext = start < maxStart;

  const getEmbedUrl = (url: string) => {
    if (url.includes("player.vimeo.com/video/")) {
      return url.includes("?") ? `${url}&autoplay=1` : `${url}?autoplay=1`;
    }

    const match =
      url.match(/\/videos\/(\d+)/) ||
      url.match(/\/video\/(\d+)/) ||
      url.match(/vimeo\.com\/(\d+)/);

    if (!match?.[1]) {
      return url;
    }

    return `https://player.vimeo.com/video/${match[1]}?autoplay=1`;
  };

  return (
    <Box >

      <Box
        sx={{
          width: totalWidth,
          overflow: "hidden",
          position: "relative",
          mx: "auto",
          mb: { xs: 5, md: 4 },
          borderRadius: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            transition: "transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: `translateX(-${start * (cardWidth + gap)}px)`,
            gap: `${gap}px`,
          }}
        >
          {videos.map((video, idx) => (
            <Box key={idx} sx={{ minWidth: cardWidth, maxWidth: cardWidth, flex: "0 0 auto" }}>
              <VideoCard
                video={video}
                onPlay={(url, title) => setActiveVideo({ url, title })}
              />
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "center" ,gap: "32px"}}>
        <IconButton onClick={handlePrev} disabled={!canPrev} aria-label="Previous videos" size="small" sx={{ borderRadius: "100%" }}>
          <ArrowBackIcon />
        </IconButton>
        <IconButton onClick={handleNext} disabled={!canNext} aria-label="Next videos" size="small" sx={{ borderRadius: "100%" }}>
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      <Dialog
        open={Boolean(activeVideo)}
        onClose={() => setActiveVideo(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: "#000",
            width: { xs: "92vw", sm: "420px" },
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={() => setActiveVideo(null)}
            aria-label="Close video"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              color: "#fff",
              backgroundColor: "rgba(0,0,0,0.55)",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          {activeVideo && (
            <Box
              sx={{
                width: "100%",
                position: "relative",
                height: 0,
                pb: "177.778%",
                "@supports (aspect-ratio: 9 / 16)": {
                  height: "auto",
                  pb: 0,
                  aspectRatio: "9 / 16",
                },
              }}
            >
              <Box
                component="iframe"
                title={activeVideo.title}
                src={getEmbedUrl(activeVideo.url)}
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                allowFullScreen
                sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
